/** Subscribe through the command queue so mounting before the widget boots is safe. */
export const subscribeHelplyUnread = listener => {
  if (typeof window === 'undefined' || typeof window.Helply !== 'function') {
    return () => {}
  }
  let active = true
  const onUnread = unread => {
    if (active) listener(unread === true)
  }
  window.Helply('on', 'unreadChange', onUnread)
  return () => {
    active = false
    // The real command replaces the loading stub, so resolve it again on cleanup.
    if (typeof window.Helply === 'function') {
      window.Helply('off', 'unreadChange', onUnread)
    }
  }
}

// Fetched fresh on every call, never from the store: Groove signs the token for two hours, and the widget
// calls the provider when it sends an identify and again whenever the token runs out — a token read once
// at page load would be refused on every help click past the second hour, and the agent would chat
// anonymously with nothing said.
const helplyIdentifyTokenQuery = `
  query HelplyWidgetIdentifyToken {
    me {
      id
      helplyWidgetIdentifyToken
    }
  }
`

// The widget awaits the provider before the identify goes out — and, when it refreshes, before the request
// that needed the token — so a hung fetch here would hang those. Bounded like the Copilot grant fetch.
export const IDENTIFY_TOKEN_FETCH_TIMEOUT_MS = 15000

/**
 * The token provider `Helply('identify', { token })` takes: resolves the current agent's signed identify
 * token, or null when the environment has no widget secret (then the identify is anonymous, as before).
 * Rejects on a failed or timed-out request — the widget warns and leaves the visitor's identity as it was,
 * which is what a transient failure should do; a null here would instead replace a verified session with
 * an unverified one. `graphql` is injected for the tests; the app passes `api/app_graphql`.
 */
export const createHelplyIdentifyTokenProvider = (
  graphql,
  { timeoutMs = IDENTIFY_TOKEN_FETCH_TIMEOUT_MS } = {}
) => async () => {
  let timeoutId
  const onTimeout = new Promise((resolve, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error('Helply identify token fetch timed out')),
      timeoutMs
    )
  })
  try {
    // The client reads the auth token itself; its first argument is ignored.
    const response = await Promise.race([
      graphql(null, helplyIdentifyTokenQuery),
      onTimeout,
    ])
    const data = response && response.json && response.json.data
    return (data && data.me && data.me.helplyWidgetIdentifyToken) || null
  } finally {
    clearTimeout(timeoutId)
  }
}
